import classNames from 'classnames';
import type { Stage as KonvaStage } from 'konva/lib/Stage';
import React, { useMemo, useRef, useState, useCallback } from 'react';

import { KonvaImagePromisesProvider } from 'common/components/RewindModal/Konvas/KonvaImagePromisesProvider';
import ShareableScreenBilanUser from 'common/components/RewindModal/Konvas/ShareableScreenBilanUser';
import Slides from 'common/components/RewindModal/Slides';
import {
  MEMBER_CARD_MOVIE,
  MEMBER_CARD_SERIES,
  MEMBER_CARD_SERIES_MOVIE
} from 'common/configuration/constants';
import { shardUrl } from 'common/tools/network/url';

import styles from '../styles.module.scss';
import BottomActions from './component/BottomActions';

type BilanData = {
  total_runtime_hours?: string;
  image_runtime?: string;
  movie_count?: number;
  series_count?: number;
  image_comparison?: string;
  image_favorite_actor?: string;
  image_user_ranking?: string;
};

type ScreenBilanUserProps = {
  data?: BilanData;
  onNext: () => void;
  isLastStep: boolean;
  trackingScreenName?: string;
  userNickname?: string;
};

const ScreenBilanUserContent = ({
  data,
  onNext,
  isDownloadReady,
  userNickname
}: ScreenBilanUserProps & { isDownloadReady: boolean }) => {
  const stageRef = useRef<KonvaStage>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const movieCount = data?.movie_count ?? 0;
  const seriesCount = data?.series_count ?? 0;

  const memberType =
    movieCount > seriesCount
      ? 'movie'
      : seriesCount > movieCount
        ? 'series'
        : 'seriemovie';
  const memberCardImage =
    memberType === 'movie'
      ? MEMBER_CARD_MOVIE
      : memberType === 'series'
        ? MEMBER_CARD_SERIES
        : MEMBER_CARD_SERIES_MOVIE;

  const imageUrls = useMemo(() => {
    const buildUrl = (imageName: string | null | undefined) => {
      if (!imageName) return null;
      const path = `/commons/v9/allocine/flashback/${imageName}.png`;
      return shardUrl(path);
    };

    return {
      runtime: buildUrl(data?.image_runtime),
      comparison: buildUrl(data?.image_comparison),
      favoriteActor: buildUrl(data?.image_favorite_actor),
      userRanking: buildUrl(data?.image_user_ranking)
    };
  }, [data]);

  const handleDownload = useCallback(async () => {
    if (!isDownloadReady || isDownloading || !stageRef.current) {
      return;
    }

    setIsDownloading(true);

    setTimeout(() => {
      try {
        const stage = stageRef.current;
        if (!stage) {
          throw new Error('Konva stage is not available.');
        }

        const dataURL = stage.toDataURL({ pixelRatio: 2 });

        const link = document.createElement('a');
        link.download = 'mon-bilan-allocine-2025.png';
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        // Silently handle download errors
        // eslint-disable-next-line no-console
        console.error('Error generating image:', error);
      } finally {
        setIsDownloading(false);
      }
    }, 100);
  }, [isDownloadReady, isDownloading]);

  return (
    <>
      <Slides>
        <div className={classNames(styles.contentLayout, styles.topLayout)}>
          <div className={styles.bilanWrapper}>
            {data?.total_runtime_hours && (
              <>
                <div className={styles.bilanHours}>
                  {data.total_runtime_hours} h
                </div>
                <div className={styles.bilanHoursText}>de visionnage</div>
              </>
            )}

            <div className={styles.bilanContentWrapper}>
              <div className={styles.comparisonContainer}>
                {imageUrls.runtime && (
                  <div className={styles.polaroidImageWrapper}>
                    <img
                      src={imageUrls.runtime}
                      alt="Badge temps de visionnage"
                      className={styles.polaroidImage}
                    />
                  </div>
                )}
                {imageUrls.comparison && (
                  <div className={styles.comparisonBadgeWrapper}>
                    <img
                      src={imageUrls.comparison}
                      alt="Badge comparaison"
                      className={styles.comparisonBadge}
                    />
                  </div>
                )}
              </div>
              <div className={styles.memberCardImageWrapper}>
                <div className={styles.memberCardNickname}>
                  {userNickname ?? 'Passionné(e)'}
                </div>
                <img
                  src={memberCardImage}
                  alt="Member Card"
                  className={styles.memberCardImage}
                />
                {imageUrls.favoriteActor && (
                  <div className={styles.polaroidBadge}>
                    <img
                      src={imageUrls.favoriteActor}
                      alt="Badge acteur favori"
                      className={styles.polaroidBadgeImg}
                    />
                  </div>
                )}
                {imageUrls.userRanking && (
                  <div className={styles.thanksBadge}>
                    <img
                      src={imageUrls.userRanking}
                      alt="Badge remerciement"
                      className={styles.thanksBadgeImg}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        <BottomActions
          showShare={true}
          shareText={isDownloading ? 'Génération...' : 'Partager mon bilan'}
          onShareClick={handleDownload}
          isShareDisabled={!isDownloadReady || isDownloading}
          showNext={true}
          onNextClick={onNext}
        />
      </Slides>

      <ShareableScreenBilanUser
        ref={stageRef}
        data={data ?? {}}
        userNickname={userNickname}
      />
    </>
  );
};

const ScreenBilanUser = (props: ScreenBilanUserProps) => {
  const [isDownloadReady, setIsDownloadReady] = useState(false);

  return (
    <KonvaImagePromisesProvider onDownloadableChange={setIsDownloadReady}>
      <ScreenBilanUserContent {...props} isDownloadReady={isDownloadReady} />
    </KonvaImagePromisesProvider>
  );
};

export default ScreenBilanUser;
